import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DocumentFragment} from 'app/fragment/types';
import {TargetVersionType} from 'app/fragment/types/reference/internal-reference-state';
import {InternalReferenceStateService} from 'app/fragment/types/reference/internal-reference-state.service';
import {CarsAction} from 'app/permissions/types/permissions';
import {DocumentService} from 'app/services/document.service';
import {UUID} from 'app/utils/uuid';
import {ReferenceSummaryRow} from '../types/reference-summary-row';
import {ReferenceSummaryType} from '../types/reference-summary-type';

interface PublicationStatusInformation {
  icon: string;
  text: string;
}

@Component({
  selector: 'cars-reference-summary-table',
  templateUrl: './reference-summary-table.component.html',
  styleUrls: ['./reference-summary-table.component.scss'],
})
export class ReferenceSummaryTableComponent implements OnInit {
  @Input() public direction: ReferenceSummaryType;
  @Input() public referenceRows: ReferenceSummaryRow[];
  @Input() public fullDocumentTree: DocumentFragment;
  @Output() public onToggleTargetVersion = new EventEmitter<void>();

  public readonly ReferenceSummaryType: typeof ReferenceSummaryType = ReferenceSummaryType;
  public readonly CarsAction: typeof CarsAction = CarsAction;

  public documentId: UUID;

  constructor(
    private _internalReferenceStateService: InternalReferenceStateService,
    private _documentService: DocumentService
  ) {}

  public ngOnInit(): void {
    this.documentId = this._documentService.getSelected().id;
  }

  public generateLinkToDocument(row: ReferenceSummaryRow): string {
    const linkDocumentId: UUID =
      this.direction === ReferenceSummaryType.INCOMING ? row.sourceDocumentId : row.targetDocumentId;
    return '/documents/' + linkDocumentId.value;
  }

  public generateReferenceStateIcon(row: ReferenceSummaryRow, isLive: boolean): string {
    if (
      (!!isLive && row.liveNumberOfInvalidReferences > 0) ||
      (!isLive && row.lastPublishedNumberOfInvalidReferences > 0)
    ) {
      return 'warning';
    }
    return 'check_circle';
  }

  public generateLinkToVersion(row: ReferenceSummaryRow): string {
    return `/documents/${row.lastPublishedVersionTag.fragmentId.value}/versions/${row.lastPublishedVersionTag.versionId.value}`;
  }

  public formatVersionNumberForDisplay(row: ReferenceSummaryRow): string {
    const parts: string[] = row.lastPublishedVersionTag?.versionNumber?.split('.');
    return parts?.slice(0, 3).join('.') || '';
  }

  public generatePublicationStatusInformation(row: ReferenceSummaryRow): PublicationStatusInformation {
    if (row.referenceSummaryType === ReferenceSummaryType.SAME_DOCUMENT) {
      if (row.liveNumberOfInvalidReferences > 0) {
        return {icon: 'warning', text: 'There are references that need to be reviewed'};
      }
      return {icon: 'check_circle', text: 'All references up to date'};
    }

    if (row.referenceSummaryType === ReferenceSummaryType.INCOMING) {
      if (row.liveNumberOfInvalidReferences === 0 && row.lastPublishedNumberOfInvalidReferences > 0) {
        return {icon: 'check_circle', text: 'Joint publication required'};
      }
      if (row.liveNumberOfInvalidReferences > 0 && row.lastPublishedNumberOfInvalidReferences > 0) {
        return {icon: 'error', text: 'Joint publication required and references to be refreshed/reviewed'};
      }
      if (
        row.liveNumberOfInvalidReferences > 0 &&
        (row.lastPublishedNumberOfInvalidReferences === null || row.lastPublishedNumberOfInvalidReferences === 0)
      ) {
        return {icon: 'warning', text: 'References to be refreshed if to be published at the same time'};
      }
      if (
        row.liveNumberOfInvalidReferences === 0 &&
        (row.lastPublishedNumberOfInvalidReferences === null || row.lastPublishedNumberOfInvalidReferences === 0)
      ) {
        return {icon: 'check_circle', text: 'Document not affected by this publication'};
      }
    }

    if (row.referenceSummaryType === ReferenceSummaryType.OUTGOING) {
      if (
        row.liveNumberOfInvalidReferences === 0 &&
        (row.lastPublishedNumberOfInvalidReferences === null || row.lastPublishedNumberOfInvalidReferences > 0)
      ) {
        return {icon: 'check_circle', text: 'Joint publication required'};
      }
      if (
        row.liveNumberOfInvalidReferences > 0 &&
        (row.lastPublishedNumberOfInvalidReferences === null || row.lastPublishedNumberOfInvalidReferences > 0)
      ) {
        return {icon: 'error', text: 'Joint publication required and references to be refreshed/reviewed'};
      }
      if (row.liveNumberOfInvalidReferences > 0 && row.lastPublishedNumberOfInvalidReferences === 0) {
        return {icon: 'check_circle', text: 'Document not affected by this publication'};
      }
      if (row.liveNumberOfInvalidReferences === 0 && row.lastPublishedNumberOfInvalidReferences === 0) {
        return {
          icon: 'check_circle',
          text: 'Publication of other document is not required as part of this publication',
        };
      }
    }
  }

  public toggleTargetVersion(ref: ReferenceSummaryRow): void {
    const desiredTargetVersion = ref.targetVersionIsLive ? TargetVersionType.LAST_PUBLISHED : TargetVersionType.LIVE;

    this._internalReferenceStateService
      .updateTargetVersion(ref.sourceDocumentId, ref.targetDocumentId, desiredTargetVersion)
      .then((_res) => this.onToggleTargetVersion.emit());
  }
}
